import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Backdrop, Box, LinearProgress, Stack } from '@mui/material';
import { Typography } from '@malash-office/ui-components';

export interface BusyState {
  title: string;
  subtitle?: string;
  /** 0-100 for a measured bar; leave out while the server works and there is nothing to measure. */
  progress?: number;
}

interface BusyContextValue {
  showBusy: (state: BusyState) => void;
  /** Patch the message/progress of the overlay already on screen. */
  updateBusy: (patch: Partial<BusyState>) => void;
  hideBusy: () => void;
}

const BusyContext = createContext<BusyContextValue>({
  showBusy: () => undefined,
  updateBusy: () => undefined,
  hideBusy: () => undefined
});

/**
 * App-wide blocking overlay, for the operations that must not be interrupted or raced — today the
 * customers Excel import, which rewrites the rows the screen behind it is displaying.
 *
 * Deliberately blocking rather than a local spinner: the point is that nothing else in the system can
 * be touched (menu, table, navigation) until the write has finished.
 */
export const useBusy = () => useContext(BusyContext);

export const BusyProvider = ({ children }: { children: ReactNode }) => {
  const [busy, setBusy] = useState<BusyState | null>(null);

  const showBusy = useCallback((state: BusyState) => setBusy(state), []);
  const updateBusy = useCallback(
    (patch: Partial<BusyState>) => setBusy(current => (current ? { ...current, ...patch } : current)),
    []
  );
  const hideBusy = useCallback(() => setBusy(null), []);

  const value = useMemo(() => ({ showBusy, updateBusy, hideBusy }), [showBusy, updateBusy, hideBusy]);

  return (
    <BusyContext.Provider value={value}>
      {children}
      <Backdrop
        open={!!busy}
        // Above MUI's modal layer, so it also blocks the dialog that started the operation.
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      >
        <Box sx={{ bgcolor: 'common.white', borderRadius: 2, p: 3, minWidth: 320, maxWidth: 420 }}>
          <Stack spacing={1.5}>
            <Typography variant="h4" color="text.primary">
              {busy?.title}
            </Typography>
            {busy?.subtitle && (
              <Typography variant="body" color="text.secondary">
                {busy.subtitle}
              </Typography>
            )}
            <LinearProgress
              variant={busy?.progress === undefined ? 'indeterminate' : 'determinate'}
              value={busy?.progress}
            />
          </Stack>
        </Box>
      </Backdrop>
    </BusyContext.Provider>
  );
};
